import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Edit, Mail, Trash } from "lucide-react";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { useJobDetail } from "@/hooks/jobs/useJobDetail";
import { generateJobDescriptionEmail, createJobDescriptionPDF } from "@/utils/pdf/jobDescriptionPdf";
import { toast } from "@/hooks/use-toast";
import { JobOfferEditModal } from "@/components/jobs/JobOfferEditModal";
import { supabase } from "@/integrations/supabase/client";
import { useHistoryOperations } from "@/hooks/useHistoryOperations";
import { JobFormData } from "./types/jobTypes";
import { useTranslation } from "react-i18next";
import { 
	AlertDialog, 
	AlertDialogContent, 
	AlertDialogHeader, 
	AlertDialogTitle, 
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogAction,
	AlertDialogCancel
} from "@/components/ui/alert-dialog";

interface JobDetailHeaderProps {
  jobId: string | undefined;
  refreshData?: () => void;
}

export const JobDetailHeader = ({ jobId, refreshData }: JobDetailHeaderProps) => {
  const { t } = useTranslation();
  const { job, refetch } = useJobDetail(jobId);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const { logHistory } = useHistoryOperations('job');
  const [isSaving, setIsSaving] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const navigate = useNavigate();

  // Remove auto-refresh to prevent flickering
  // We'll only refresh when explicitly needed (after saving)

  const handleSendByEmail = async () => {
    if (!job) return;

    try {
      const { mailtoLink } = generateJobDescriptionEmail({
        ...job,
        description: job.description || ""
      });

      await createJobDescriptionPDF({
        ...job,
        description: job.description || ""
      });

      window.open(mailtoLink, '_blank');

      toast({
        title: t("jobs.detail.emailCreated", "Email created"),
        description: t("jobs.detail.emailClientOpened", "Your email client has been opened with the job description. Please add recipients."),
      });
    } catch (error) {
      console.error("Error sending email:", error);
      toast({
        title: t("common.error", "Error"),
        description: t("jobs.detail.emailCreationFailed", "Failed to create email with job description"),
        variant: "destructive",
      });
    }
  };

  // Helper function to normalize language requirements to an array
  const normalizeLanguageRequirements = (langReq: string | string[] | null | undefined): string[] => {
    if (!langReq) return [];
    if (Array.isArray(langReq)) return langReq;
    return [langReq];
  };

  // Helper to translate a language name
  const translateLanguageName = (language: string): string => {
    const lowercase = language.toLowerCase();
    const supportedLanguages = ['english', 'dutch', 'french', 'german', 'spanish', 'italian', 'other'];
    
    if (supportedLanguages.includes(lowercase)) {
      return t(`jobs.formWizard.languages.${lowercase}`, language);
    }
    
    return language;
  };

  const handleUpdateJob = async (updatedJob: JobFormData) => {
    if (!jobId) {
      console.error("No job ID provided for update");
      toast({
        title: t("common.error", "Error"),
        description: t("jobs.detail.noJobId", "Cannot update job: No job ID provided"),
        variant: "destructive",
      });
      return false;
    }

    setIsSaving(true);

    try {
      // Process the arrays from comma-separated strings
      const skillsArray = updatedJob.skills
        ? updatedJob.skills.split(',').map(skill => skill.trim()).filter(Boolean)
        : [];

      const hardSkillsArray = updatedJob.hardSkills
        ? updatedJob.hardSkills.split(',').map(skill => skill.trim()).filter(Boolean)
        : [];

      const softSkillsArray = updatedJob.softSkills
        ? updatedJob.softSkills.split(',').map(skill => skill.trim()).filter(Boolean)
        : [];

      const cultureTagsArray = updatedJob.cultureTags
        ? updatedJob.cultureTags.split(',').map(tag => tag.trim()).filter(Boolean)
        : [];
        
      // Normalize language requirements
      const languageRequirementsArray = normalizeLanguageRequirements(updatedJob.languageRequirements);

      // Normalize null/undefined values for the database
      const endDate = updatedJob.endDate && typeof updatedJob.endDate === 'object'
        ? null
        : updatedJob.endDate || null;

      const extraLang = updatedJob.extraLang && typeof updatedJob.extraLang === 'object'
        ? null
        : updatedJob.extraLang || null;

      // First get the current job data to compare for history logging
      const { data: currentJob, error: fetchError } = await supabase
        .from('jobs')
        .select('*')
        .eq('id', jobId)
        .single();

      if (fetchError) {
        console.error("Error fetching current job data:", fetchError);
        throw fetchError;
      }

      // Clean up potential undefined values for database
      const jobUpdateData = {
        title: updatedJob.title || null,
        company_id: updatedJob.companyId || null,
        description: updatedJob.description || null,
        ai_summary: updatedJob.aiSummary || null,
        skills: skillsArray.length > 0 ? skillsArray : null,
        contact_id: updatedJob.contactId || null,

        employment_type: updatedJob.employmentType || null,
        contract_duration: updatedJob.contractDuration || null,
        start_date: updatedJob.startDate || null,
        end_date: endDate,
        seniority_level: updatedJob.seniorityLevel || null,
        budget: updatedJob.budget || null,
        budget_freelance: updatedJob.budgetFreelance || null,
        location_type: updatedJob.locationType || null,
        workplace_breakdown: updatedJob.workplaceBreakdown || null,
        hard_skills: hardSkillsArray.length > 0 ? hardSkillsArray : null,
        soft_skills: softSkillsArray.length > 0 ? softSkillsArray : null,
        education_level: updatedJob.educationLevel || null,
        years_of_experience: updatedJob.yearsOfExperience || null,
        language_requirements: languageRequirementsArray.length > 0 ? languageRequirementsArray.join(', ') : null,
        extra_lang: extraLang,
        culture_tags: cultureTagsArray.length > 0 ? cultureTagsArray : null,
        application_deadline: updatedJob.applicationDeadline || null,
        job_posting_date: updatedJob.jobPostingDate || null,
        source: updatedJob.source || null,
        assigned_recruiter: updatedJob.assignedRecruiter || null,
        employment_classification: updatedJob.employmentClassification || null
      };

      const { error: updateError } = await supabase
        .from('jobs')
        .update(jobUpdateData)
        .eq('id', jobId);


      if (updateError) {
        console.error("Error from Supabase:", updateError);
        toast({
          title: t("common.error", "Error"),
          description: t("jobs.detail.updateFailed", "Failed to update job offer: ") + updateError.message,
          variant: "destructive",
        });
        return false;
      }

      console.log('Triggering AI analysis for job ID:', jobId);

      console.log("Calling analyse-job-offer function with:", {
        jobId
      });

      const aiResponse = await supabase.functions.invoke('analyse-job-offer', {
        body: {
          jobOfferId: jobId
        }
      });

      if (aiResponse.error) {
        console.error("AI analysis error:", aiResponse.error);
        throw new Error(`AI Analysis Failed: ${aiResponse.error.message || 'Unknown error'}`);
      }

      console.log("AI analysis completed successfully:", aiResponse.data);


      // Log specific field changes to history
      if (currentJob) {
        // Log title changes
        if (updatedJob.title !== currentJob.title) {
          await logHistory({
            entityId: jobId,
            entityType: "job",
            action: "updated",
            description: t("jobs.detail.history.title", "Job Title"),
            change: {
              field: t("jobs.detail.history.title", "Job Title"),
              oldValue: currentJob.title,
              newValue: updatedJob.title
            }
          });
        }

        // Log description changes
        if (updatedJob.description !== currentJob.description) {
          await logHistory({
            entityId: jobId,
            entityType: "job",
            action: "updated",
            description: t("jobs.detail.history.description", "Job Description"),
            change: {
              field: t("jobs.detail.history.description", "Job Description"),
              oldValue: currentJob.description,
              newValue: updatedJob.description
            }
          });
        }

        // Log employment type changes
        if (updatedJob.employmentType !== currentJob.employment_type) {
          await logHistory({
            entityId: jobId,
            entityType: "job",
            action: "updated",
            description: t("jobs.detail.history.employmentType", "Employment Type"),
            change: {
              field: t("jobs.detail.history.employmentType", "Employment Type"),
              oldValue: currentJob.employment_type,
              newValue: updatedJob.employmentType
            }
          });
        }

        // Log seniority level changes
        if (updatedJob.seniorityLevel !== currentJob.seniority_level) {
          await logHistory({
            entityId: jobId,
            entityType: "job",
            action: "updated",
            description: t("jobs.detail.history.seniorityLevel", "Seniority Level"),
            change: {
              field: t("jobs.detail.history.seniorityLevel", "Seniority Level"),
              oldValue: currentJob.seniority_level,
              newValue: updatedJob.seniorityLevel
            }
          });
        }

        // Log budget changes
        if (updatedJob.budget !== currentJob.budget) {
          await logHistory({
            entityId: jobId,
            entityType: "job",
            action: "updated",
            description: t("jobs.detail.history.budget", "Budget"),
            change: {
              field: t("jobs.detail.history.budget", "Budget"),
              oldValue: currentJob.budget,
              newValue: updatedJob.budget
            }
          });
        }

        // Log location type changes
        if (updatedJob.locationType !== currentJob.location_type) {
          await logHistory({
            entityId: jobId,
            entityType: "job",
            action: "updated",
            description: t("jobs.detail.history.locationType", "Location Type"),
            change: {
              field: t("jobs.detail.history.locationType", "Location Type"),
              oldValue: currentJob.location_type,
              newValue: updatedJob.locationType
            }
          });
        }

        // Compare array-based fields (converting null to empty array for comparison)
        const currentSkills = currentJob.skills || [];
        const updatedSkills = skillsArray || [];

        if (JSON.stringify(currentSkills) !== JSON.stringify(updatedSkills)) {
          await logHistory({
            entityId: jobId,
            entityType: "job",
            action: "updated",
            description: t("jobs.detail.history.skills", "Skills"),
            change: {
              field: t("jobs.detail.history.skills", "Skills"),
              oldValue: currentSkills.join(", "),
              newValue: updatedSkills.join(", ")
            }
          });
        }

        // Compare language requirements
        const currentLanguages = currentJob.language_requirements || [];
        const currentLanguagesNormalized = normalizeLanguageRequirements(currentLanguages)
          .map(lang => translateLanguageName(lang));
        
        const updatedLanguagesNormalized = languageRequirementsArray
          .map(lang => translateLanguageName(lang));
        
        if (JSON.stringify(currentLanguagesNormalized) !== JSON.stringify(updatedLanguagesNormalized)) {
          await logHistory({
            entityId: jobId,
            entityType: "job",
            action: "updated",
            description: t("jobs.detail.history.languageRequirements", "Language Requirements"),
            change: {
              field: t("jobs.detail.history.languageRequirements", "Language Requirements"),
              oldValue: currentLanguagesNormalized.join(", "),
              newValue: updatedLanguagesNormalized.join(", ")
            }
          });
        }

        // Log other fields as needed
        // Education level
        if (updatedJob.educationLevel !== currentJob.education_level) {
          await logHistory({
            entityId: jobId,
            entityType: "job",
            action: "updated",
            description: t("jobs.detail.history.educationLevel", "Education Level"),
            change: {
              field: t("jobs.detail.history.educationLevel", "Education Level"),
              oldValue: currentJob.education_level,
              newValue: updatedJob.educationLevel
            }
          });
        }

        // Years of experience
        if (updatedJob.yearsOfExperience !== currentJob.years_of_experience) {
          await logHistory({
            entityId: jobId,
            entityType: "job",
            action: "updated",
            description: t("jobs.detail.history.yearsOfExperience", "Years of Experience"),
            change: {
              field: t("jobs.detail.history.yearsOfExperience", "Years of Experience"),
              oldValue: currentJob.years_of_experience,
              newValue: updatedJob.yearsOfExperience
            }
          });
        }
      }

      // Only now refresh the job data after successful update
      if (refreshData) {
        console.log("Triggering manual refresh after successful save");
        refreshData();
      } else if (refetch) {
        console.log("Calling refetch after successful save");
        await refetch();
      }

      setIsEditModalOpen(false);

      toast({
        title: t("common.success", "Success"),
        description: t("jobs.detail.updateSuccess", "Job offer updated successfully"),
      });

      return true;
    } catch (error) {
      console.error("Error updating job:", error);
      toast({
        title: t("common.error", "Error"),
        description: t("jobs.detail.updateFailed", "Failed to update job offer: ") + (error instanceof Error ? error.message : t("common.unknownError", "Unknown error")),
        variant: "destructive",
      });
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteJob = async () => {
    if (!jobId) return;

    setIsDeleting(true);

    try {
      // First log the deletion to history
      await logHistory({
        entityId: jobId,
        entityType: "job",
        action: "deleted",
        description: t("jobs.detail.history.jobDeleted", "Job Deleted"),
        change: {
          field: t("jobs.detail.history.entireJob", "Entire Job"),
          oldValue: job?.title || t("jobs.detail.jobOffer", "Job Offer"),
          newValue: t("jobs.detail.deleted", "Deleted")
        }
      });

      // Delete associated matches first to maintain referential integrity
      const { error: matchesError } = await supabase
        .from('matches')
        .delete()
        .eq('job_id', jobId);

      if (matchesError) {
        console.error("Error deleting job matches:", matchesError);
      }

      // Delete job comments
      const { error: commentsError } = await supabase
        .from('job_comments')
        .delete()
        .eq('job_id', jobId);
      
      if (commentsError) {
        console.error("Error deleting job comments:", commentsError);
      }

      // Finally delete the job itself
      const { error } = await supabase
        .from('jobs')
        .delete()
        .eq('id', jobId);

      if (error) {
        console.error("Error deleting job:", error);
        toast({
          title: t("common.error", "Error"),
          description: t("jobs.detail.deleteError", "Failed to delete job offer: ") + error.message,
          variant: "destructive",
        });
        return;
      }

      toast({
        title: t("common.success", "Success"),
        description: t("jobs.detail.deleteSuccess", "Job offer deleted successfully"),
      });

      // Redirect to the jobs list page after successful deletion
      navigate('/jobs');
      
    } catch (error) {
      console.error("Error in job deletion:", error);
      toast({
        title: t("common.error", "Error"),
        description: t("jobs.detail.deleteError", "Failed to delete job offer"),
        variant: "destructive",
      });
    } finally {
      setIsDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  return (
    <>
      <PageHeader
        title={t("jobs.detail.title", "Job Offer")}
        actions={
          <div className="flex gap-2">
            <Button
              variant="outline"
              className="flex items-center gap-2"
              onClick={handleSendByEmail}
              disabled={!job}
            >
              <Mail className="h-4 w-4" />
              <span>{t("jobs.detail.sendByEmail", "Send by Email")}</span>
            </Button>
            <Button
              variant="outline"
              className="flex items-center gap-2"
              onClick={() => setIsEditModalOpen(true)}
              disabled={isSaving}
            >
              <Edit className="h-4 w-4" />
              <span>{isSaving ? t("common.saving", "Saving...") : t("common.edit", "Edit")}</span>
            </Button>
            <Button 
              variant="outline" 
              className="flex items-center gap-2 text-red-600 hover:text-red-700"
              onClick={() => setShowDeleteConfirm(true)}
              disabled={isDeleting}
            >
              <Trash className="h-4 w-4" />
              <span>
                {isDeleting 
                  ? t("common.deleting", "Deleting...") 
                  : t("common.delete", "Delete")
                }
              </span>
            </Button>
          </div>
        }
      />

      <div className="mb-6">
        <Link to="/jobs" className="text-digger-blue hover:underline flex items-center">
          <ArrowLeft className="h-4 w-4 mr-1" />
          {t("jobs.detail.backToJobs", "Back to job offers")}
        </Link>
      </div>

      {job && (
        <JobOfferEditModal
          isOpen={isEditModalOpen}
          onClose={() => setIsEditModalOpen(false)}
          onSave={handleUpdateJob}
          job={{
            ...job,
            // Ensure language_requirements is always a string for the edit modal
            languageRequirements: normalizeLanguageRequirements(job.languageRequirements)
              .map(lang => translateLanguageName(lang)).join(', ')
          }}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <AlertDialogContent className="sm:max-w-[425px]">
          <AlertDialogHeader>
            <AlertDialogTitle>{t("jobs.detail.confirmDelete", "Confirm Deletion")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("jobs.detail.deleteWarning", "Are you sure you want to delete this job offer? This action cannot be undone and will remove all associated matches and comments.")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("common.cancel", "Cancel")}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteJob}
              className="bg-red-600 hover:bg-red-700 text-white"
              disabled={isDeleting}
            >
              {isDeleting ? t("common.deleting", "Deleting...") : t("common.delete", "Delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
